/** 库位单行展示：编码与名称相同则只显示一次，避免「H02 - H02」 */
export const formatStorageLocationDisplay = (loc) => {
  if (!loc) return '-'
  const code = (loc.location_code || '').trim()
  const name = (loc.location_name || '').trim()
  if (!code && !name) return '-'
  if (code === name) return code || name
  if (code && name) return `${code}（${name}）`
  return name || code
}

export const formatNumber = (num) => {
  if (num === null || num === undefined) return '0.0000'
  return parseFloat(num).toFixed(4)
}

export const formatDateTime = (dateTime) => {
  if (!dateTime) return ''
  return new Date(dateTime).toLocaleString('zh-CN')
}

export const formatDate = (dateString) => {
  if (!dateString) return '-'
  try {
    const date = new Date(dateString)
    return date.toLocaleDateString('zh-CN')
  } catch (error) {
    return dateString
  }
}

export const getTransactionTypeTagType = (operationType) => {
  const types = {
    'in': 'success',
    'out': 'danger',
    'adjust': 'warning',
    'transfer': 'primary'
  }
  return types[operationType] || 'info'
}

const finishedProductColorText = (row) => {
  const fc = row?.processing_finished_color
  if (fc?.display) return fc.display
  const cn = fc?.internal_name_cn
  const en = fc?.internal_name_en
  if (cn || en) return [cn, en].filter(Boolean).join(' / ')
  return ''
}

/** 交易摘要：完整交易类型名 + 材料与数量；出库类附带对方信息 */
export const generateSummary = (row) => {
  if (!row.transaction_type) return ''

  const fullTypeName = row.transaction_type.type_name || row.transaction_type.type_code || ''
  const materialName = row.material_identity?.material_category?.internal_name || '未知材料'
  const quantity = formatNumber(row.quantity)
  const unit = row.unit?.unit_name || ''
  const finishedColor = finishedProductColorText(row)
  const colorPart = finishedColor ? ` · 成品色 ${finishedColor}` : ''

  let party = ''
  if (row.outbound_object?.name) {
    const t = row.outbound_object.type
    const role = t === 'customer' ? '客户' : t === 'processor' ? '加工厂' : t === 'internal' ? '内部' : '对方'
    party = ` · ${role}：${row.outbound_object.name}`
  } else if (row.outbound_object_name) {
    party = ` · 对方：${row.outbound_object_name}`
  }

  return `${fullTypeName}｜${materialName} ${quantity}${unit}${colorPart}${party}`
}

/**
 * 备注展示：优先用后端 notes_display；否则前端拼客户/供应商。
 */
export const formatNotesWithCounterparty = (row) => {
  if (row?.notes_display) return row.notes_display
  const raw = (row?.notes || '').trim()
  const party = getRelatedPartyDescription(row)
  if (!raw) return party || null
  if (!party) return raw
  // 备注里已含客户/供应商名则不重复拼接
  const bare = party.replace(/^(客户|供应商|加工厂)：/, '').trim()
  if (bare && raw.includes(bare)) return raw
  if (raw.includes(party)) return raw
  return `${party} · ${raw}`
}

export const getSupplierDisplay = (row) => {
  if (!row) return null
  if (row.supplier?.supplier_name) return row.supplier.supplier_name
  if (row.supplier_name) return row.supplier_name
  return null
}

const INTERNAL_TX_TYPE_CODES = new Set([
  'TRANSFER',
  'OVERFLOW_IN',
  'LOSS_OUT',
  'CHECK_IN',
  'CHECK_OUT',
  'QUALITY_ADJUST',
  'ADJUST',
  'ADJUST_IN',
  'ADJUST_OUT',
])

/** 报溢报损、移库、盘点、期初等内部操作：不展示交易对象 */
const isInternalWarehouseOnlyTxn = (row) => {
  if (!row) return true
  // 送货/销售类备注优先按外部交易处理（避免红冲类型码误判为内部调整）
  const notes = String(row.notes || '')
  if (
    notes.includes('送货出库') ||
    notes.includes('[delivery_oi:') ||
    (notes.includes('[红冲单]') && (notes.includes('取消送货') || String(row.reference_number || '').toUpperCase().startsWith('YWD')))
  ) {
    return false
  }
  if (row.related_order_type === 'sales' || row.related_order_type === 'purchase' || row.related_order_type === 'production') {
    return false
  }
  if (row.outbound_object?.name || row.outbound_object_name || row.customer_name) {
    const t = row.outbound_object?.type || row.outbound_object_type
    if (t !== 'internal') return false
  }
  const tc = row.transaction_type?.type_code
  const op = row.transaction_type?.operation_type
  if (INTERNAL_TX_TYPE_CODES.has(tc) || op === 'transfer' || op === 'adjust') return true
  if (row.is_adjustment) return true
  if (row.is_initial_stock) return true
  return false
}

/**
 * 交易对象：仅外部对方（客户 / 供应商 / 加工厂）；内部仓操作返回空。
 */
export const getRelatedPartyDescription = (row) => {
  if (!row || isInternalWarehouseOnlyTxn(row)) return null

  if (row.outbound_object?.name) {
    const t = row.outbound_object.type
    if (t === 'internal') return null
    if (t === 'customer') return `客户：${row.outbound_object.name}`
    if (t === 'processor') return `加工厂：${row.outbound_object.name}`
    return row.outbound_object.name
  }
  if (row.customer_name) {
    return `客户：${row.customer_name}`
  }
  if (row.outbound_object_name) {
    if (row.outbound_object_type === 'internal') return null
    if (row.outbound_object_type === 'customer') return `客户：${row.outbound_object_name}`
    if (row.outbound_object_type === 'processor') return `加工厂：${row.outbound_object_name}`
    return row.outbound_object_name
  }

  const supplierName = getSupplierDisplay(row)
  if (supplierName) {
    return `供应商：${supplierName}`
  }

  return null
}

export const getQuantityClass = (quantity, operationType) => {
  if (operationType === 'in') return 'quantity-in'
  if (operationType === 'out') return 'quantity-out'
  if (operationType === 'transfer') return 'quantity-transfer'
  if (operationType === 'adjust') return 'quantity-adjust'
  return ''
}

export const getRelatedOrderTypeText = (type) => {
  const types = {
    'purchase': '采购',
    'sales': '销售',
    'production': '生产',
    'return': '退货',
    'adjustment': '调整'
  }
  return types[type] || type
}

export const getInitialStockTypeText = (type) => {
  if (!type) return '-'
  const types = {
    'opening_balance': '期初余额',
    'initial_purchase': '期初采购'
  }
  return types[type] || type
}

export const getWarehouseCodeClass = (code) => {
  if (!code) return 'no-warehouse-code'
  return 'warehouse-code-text'
}

export const isExpiringSoon = (expiryDate) => {
  if (!expiryDate) return false
  try {
    const expiry = new Date(expiryDate)
    const today = new Date()
    const daysUntilExpiry = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24))
    return daysUntilExpiry <= 30 && daysUntilExpiry > 0
  } catch (error) {
    return false
  }
}

export const getExpiryDateClass = (expiryDate) => {
  if (!expiryDate) return ''
  
  try {
    const expiry = new Date(expiryDate)
    const today = new Date()
    
    if (expiry < today) {
      return 'expired-date'
    } else if (isExpiringSoon(expiryDate)) {
      return 'expiring-soon-date'
    }
    return ''
  } catch (error) {
    return ''
  }
}